import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  AccessTime as AccessTimeIcon,
  CalendarMonth as CalendarMonthIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import PrimaryActionButton from '../common/PrimaryActionButton';
import SalovareDatePicker from '../common/SalovareDatePicker';
import SalovareSelectField from '../common/SalovareSelectField';
import CustomerDropdown from '../customers/CustomerDropdown';
import { fieldLabelSx, inputWrapperSx } from '../common/formFieldStyles';
import SalovarePhoneInput from '../common/SalovarePhoneInput';
import { EMAIL_CHECK_STATUS } from '../customers/CreateStoreCustomerDialog';
import StoreService from '../../services/storeService';
import ServiceComboService, { mergeCatalogCombos } from '../../services/serviceComboService';
import StoreCustomerService from '../../services/storeCustomerService';
import { DEFAULT_PHONE_COUNTRY_CODE, toPhoneDto } from '../../utils/phoneUtils';
import { createManagementAppointment } from '../../services/appointmentService';
import { getManagementBookingMembershipEntitlements } from '../../services/customerMembershipService';
import { getManagementMemberships } from '../../services/storeCustomerMembershipService';
import MembershipPlanService from '../../services/membershipPlanService';
import ServicePackageService from '../../services/servicePackageService';
import ProductSaleLinesField, { getProductLinesTotal } from '../products/ProductSaleLinesField';
import ProductService from '../../services/productService';
import { getServiceName } from '../../utils/serviceTranslations';
import { filterAgendaVisibleTeamMembers } from '../../utils/teamMemberBookingUtils';
import MembershipBookingOptionLabel, { membershipOptionLabelSx } from './MembershipBookingOptionLabel';
import { calculateBookingReviewAmountDue } from '../../utils/bookingAmountDueUtils';
import { calculateAdditionalCostAmount } from '../../utils/appointmentTimePricingUtils';
import { DEFAULT_CURRENCY, formatCurrency } from '../../utils/currencyUtils';
import {
  buildManagementAppointmentMembershipFields,
  canOfferMembershipEnrollment,
  getEnrollablePlans,
  getHeldCustomerMemberships,
  getLockedServiceIds,
  getProfessionalsForManagedBookingService,
  PACKAGE_OPTION_PREFIX,
  toPackageLockSource,
  mergeLockedServiceIds,
  resolveInitialMembershipSelection,
  toggleServiceIdWithLocks,
} from '../../utils/managementBookingMembershipUtils';
import { normalizeLanguage, DEFAULT_LANGUAGE, getLocalizedValue } from '../../constants/Languages';
import {
  applyLineDurations,
  buildPerServiceAssignments,
  computeSuggestedAssignment,
  getManagementBookingWarning,
  MANAGEMENT_START_TIME_OPTIONS,
  memberCanPerformBookingService,
  parseDurationToMinutes,
  sortServicesForBooking,
  withLineQuotes,
} from '../../utils/appointmentBookingUtils';
import AppointmentLineQuoteFields from './AppointmentLineQuoteFields';
import {
  ServiceValueMode,
  assignedServicePriceHint,
  isLineQuoteDurationInvalid,
  isSchedulableDuration,
  normalizeServiceValueMode,
} from '../../utils/serviceValueFormat';
import { matchServiceComboIds } from '../../utils/serviceComboMatch';
import { Permissions } from '../../constants/Permissions';
import { usePermissions } from '../../hooks/usePermissions';
import useStorePlan from '../../hooks/useStorePlan';
import useMediaQuery from '@mui/material/useMediaQuery';
import { alpha, useTheme } from '@mui/material/styles';
import { BRAND, BRAND_RADIUS, brandAlpha } from '../../theme/brandTokens';
import { formatCalendarDate, parseCalendarDateKey, toCalendarDateKey } from '../../utils/dateFormatUtils';
import { getApiErrorCode } from '../../utils/apiErrorCode';
import { describeInsufficientStock } from '../../utils/stockErrorUtils';
import { MANAGEMENT_ENTITLEMENT_ERROR_KEYS } from '../../utils/entitlementErrorKeys';
import PendingConsentAlert from '../consent-forms/PendingConsentAlert';

const CUSTOMER_TYPE_EXISTING = 'existing';
const CUSTOMER_TYPE_NEW = 'new';
const CUSTOMER_TYPE_ANONYMOUS = 'anonymous';
const EMPTY_SERVICE_IDS = [];
const EMPTY_APPOINTMENTS = [];

const isValidEmail = (email) => /\S+@\S+\.\S+/.test(email.trim());

const inputStyle = (hasError, isDisabled) => ({
  width: '100%',
  padding: '12px 16px',
  border: `2px solid ${hasError ? '#f44336' : BRAND.line}`,
  borderRadius: BRAND_RADIUS.button,
  fontSize: '16px',
  fontFamily: 'inherit',
  outline: 'none',
  transition: 'border-color 0.3s ease',
  backgroundColor: isDisabled ? BRAND.background : BRAND.surface,
  cursor: isDisabled ? 'not-allowed' : 'text',
});

const BookAppointmentDialog = ({
  open,
  onClose,
  storeId,
  slotContext,
  teamMembers,
  workingHoursByTeamMemberId,
  storeDetails,
  storeLanguage,
  existingAppointments = EMPTY_APPOINTMENTS,
  initialCustomer = null,
  initialServiceIds = EMPTY_SERVICE_IDS,
  initialCustomerMembershipId = null,
  initialMembershipPlanId = null,
  hideProfessionals = false,
  hideProducts = false,
  onBooked,
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const lang = normalizeLanguage(i18n.language) || DEFAULT_LANGUAGE;
  const normalizedStoreLanguage = normalizeLanguage(storeLanguage) || DEFAULT_LANGUAGE;
  const { hasAnyPermission, hasPermission } = usePermissions(storeId);
  const canSetServicePrices = hasPermission(Permissions.AppointmentsPriceUpdate);
  const { isPro, entitlements } = useStorePlan(storeId, Boolean(open && storeId));
  const allowsMemberships = isPro && entitlements?.allowsMemberships !== false;

  const canReadCustomers = hasAnyPermission([Permissions.CustomersRead]);
  const canCreateCustomers = hasAnyPermission([Permissions.CustomersCreate]);

  const [services, setServices] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [customerType, setCustomerType] = useState(CUSTOMER_TYPE_EXISTING);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedServiceIds, setSelectedServiceIds] = useState([]);
  const [lineQuotes, setLineQuotes] = useState({});
  const [serviceProfessionalMap, setServiceProfessionalMap] = useState({});
  const [detailsFromCustomer, setDetailsFromCustomer] = useState('');
  const [guestName, setGuestName] = useState('');
  const [newCustomerEmail, setNewCustomerEmail] = useState('');
  const [newCustomerName, setNewCustomerName] = useState('');
  const [newCustomerPhoneCountryCode, setNewCustomerPhoneCountryCode] = useState(DEFAULT_PHONE_COUNTRY_CODE);
  const [newCustomerPhoneNumber, setNewCustomerPhoneNumber] = useState('');
  const [emailCheckStatus, setEmailCheckStatus] = useState(EMAIL_CHECK_STATUS.IDLE);
  const [phoneCheckStatus, setPhoneCheckStatus] = useState(EMAIL_CHECK_STATUS.IDLE);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [checkingPhone, setCheckingPhone] = useState(false);
  const [existingCustomerName, setExistingCustomerName] = useState('');
  const [bookingEntitlements, setBookingEntitlements] = useState([]);
  const [loadingEntitlements, setLoadingEntitlements] = useState(false);
  const [membershipPlans, setMembershipPlans] = useState([]);
  const [customerMemberships, setCustomerMemberships] = useState([]);
  const [membershipsReady, setMembershipsReady] = useState(false);
  const [selectedMembershipId, setSelectedMembershipId] = useState('');
  const [selectedMembershipPlanId, setSelectedMembershipPlanId] = useState('');
  const [packageEntitlements, setPackageEntitlements] = useState([]);
  const [selectedServicePackageId, setSelectedServicePackageId] = useState('');
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [productLines, setProductLines] = useState([]);
  const [selectedStartTime, setSelectedStartTime] = useState('');
  const [startTimeMenuOpen, setStartTimeMenuOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const startTimeAnchorRef = useRef(null);
  const dateAnchorRef = useRef(null);
  const membershipPrefillAppliedRef = useRef(false);

  const selectedDateKey = toCalendarDateKey(selectedDate);

  useEffect(() => {
    if (!open) return;
    setSelectedStartTime(slotContext?.startTime || '');
    setSelectedDate(parseCalendarDateKey(slotContext?.date));
    setStartTimeMenuOpen(false);
    setDatePickerOpen(false);
  }, [open, slotContext?.startTime, slotContext?.date]);

  const activeTeamMembers = useMemo(
    () => filterAgendaVisibleTeamMembers(teamMembers),
    [teamMembers],
  );

  const selectedServices = useMemo(
    () => sortServicesForBooking(
      services.filter((service) => selectedServiceIds.includes(service.id)),
    ),
    [services, selectedServiceIds],
  );
  const quotedServices = useMemo(
    () => applyLineDurations(selectedServices, lineQuotes),
    [selectedServices, lineQuotes],
  );

  useEffect(() => {
    setLineQuotes((current) => {
      const next = {};
      selectedServices.forEach((service) => {
        if (service.isCombo) return;
        if (current[service.id]) {
          next[service.id] = current[service.id];
          return;
        }
        const durationMode = normalizeServiceValueMode(service.durationMode);
        const catalogMinutes = parseDurationToMinutes(service.duration);
        next[service.id] = {
          durationMinutes: durationMode === ServiceValueMode.Fixed && isSchedulableDuration(catalogMinutes)
            ? catalogMinutes
            : '',
          unitPrice: '',
          priceTouched: false,
        };
      });
      return next;
    });
  }, [selectedServices]);

  const selectedHeldMembership = useMemo(
    () => customerMemberships.find((membership) => membership.id === selectedMembershipId) || null,
    [customerMemberships, selectedMembershipId],
  );

  const selectedPlan = useMemo(
    () => membershipPlans.find((plan) => (
      plan.id === selectedMembershipPlanId
      || plan.id === selectedHeldMembership?.membershipPlanId
    )) || null,
    [membershipPlans, selectedMembershipPlanId, selectedHeldMembership],
  );

  const selectedPackageEntitlement = useMemo(
    () => packageEntitlements.find((entitlement) => entitlement.customerServicePackageId === selectedServicePackageId) || null,
    [packageEntitlements, selectedServicePackageId],
  );

  // The plan or package being redeemed keeps its services in the booking and its professionals.
  const entitlementLockSource = useMemo(
    () => selectedPlan || toPackageLockSource(selectedPackageEntitlement),
    [selectedPlan, selectedPackageEntitlement],
  );

  const lockedServiceIds = useMemo(
    () => getLockedServiceIds(entitlementLockSource),
    [entitlementLockSource],
  );

  const enrollablePlans = useMemo(
    () => getEnrollablePlans(membershipPlans, customerMemberships),
    [membershipPlans, customerMemberships],
  );

  const hideProfessionalSection = hideProfessionals || activeTeamMembers.length <= 1;
  const hasPackageOptions = customerType === CUSTOMER_TYPE_EXISTING
    && Boolean(selectedCustomer)
    && packageEntitlements.length > 0;
  const showMembershipSection = hasPackageOptions || (allowsMemberships
    && (membershipPlans.length > 0 || customerMemberships.length > 0)
    && canOfferMembershipEnrollment(customerType)
    && (customerType !== CUSTOMER_TYPE_EXISTING || Boolean(selectedCustomer)));

  const additionalCostAmount = useMemo(
    () => calculateAdditionalCostAmount({
      rules: storeDetails?.timePricing?.rules,
      date: selectedDateKey,
      startTime: selectedStartTime,
      serviceLineTotal: selectedServices.reduce((sum, service) => sum + (service.price || 0), 0),
    }),
    [storeDetails?.timePricing?.rules, selectedDateKey, selectedStartTime, selectedServices],
  );

  const productsTotal = useMemo(
    () => getProductLinesTotal(catalogProducts, productLines),
    [catalogProducts, productLines],
  );

  const amountDue = useMemo(
    () => calculateBookingReviewAmountDue({
      selectedServices,
      selectedMembershipPlanId: selectedMembershipPlanId || null,
      selectedPlan,
      selectedPlanMembership: selectedHeldMembership,
      selectedMembershipId: selectedMembershipId || null,
      bookingEntitlements,
      selectedServicePackageId: selectedServicePackageId || null,
      packageEntitlements,
      additionalCostAmount,
      productsTotal,
    }),
    [
      selectedServices,
      selectedMembershipPlanId,
      selectedPlan,
      selectedHeldMembership,
      selectedMembershipId,
      bookingEntitlements,
      selectedServicePackageId,
      packageEntitlements,
      additionalCostAmount,
      productsTotal,
    ],
  );

  const currencyCode = storeDetails?.currency || DEFAULT_CURRENCY;

  const bookingWarning = useMemo(() => {
    if (!selectedDateKey || !selectedStartTime) {
      return null;
    }

    return getManagementBookingWarning({
      store: storeDetails,
      date: selectedDateKey,
      startTime: selectedStartTime,
      selectedServices: quotedServices,
      serviceProfessionalMap,
      existingAppointments,
      prefilledTeamMemberId: slotContext?.teamMemberId,
      workingHoursByTeamMemberId,
    });
  }, [
    storeDetails,
    workingHoursByTeamMemberId,
    selectedDateKey,
    slotContext?.teamMemberId,
    selectedStartTime,
    quotedServices,
    serviceProfessionalMap,
    existingAppointments,
  ]);

  const resetForm = useCallback(() => {
    setCustomerType(canReadCustomers ? CUSTOMER_TYPE_EXISTING : (canCreateCustomers ? CUSTOMER_TYPE_NEW : CUSTOMER_TYPE_ANONYMOUS));
    setSelectedCustomer(null);
    setSelectedServiceIds([]);
    setServiceProfessionalMap({});
    setDetailsFromCustomer('');
    setGuestName('');
    setNewCustomerEmail('');
    setNewCustomerName('');
    setNewCustomerPhoneCountryCode(DEFAULT_PHONE_COUNTRY_CODE);
    setNewCustomerPhoneNumber('');
    setEmailCheckStatus(EMAIL_CHECK_STATUS.IDLE);
    setPhoneCheckStatus(EMAIL_CHECK_STATUS.IDLE);
    setCheckingEmail(false);
    setCheckingPhone(false);
    setExistingCustomerName('');
    setBookingEntitlements([]);
    setMembershipPlans([]);
    setCustomerMemberships([]);
    setMembershipsReady(false);
    setSelectedMembershipId('');
    setSelectedMembershipPlanId('');
    setPackageEntitlements([]);
    setSelectedServicePackageId('');
    setProductLines([]);
    setSelectedStartTime('');
    setStartTimeMenuOpen(false);
    setSelectedDate(null);
    setDatePickerOpen(false);
    setErrors({});
    setSubmitError(null);
    setSubmitting(false);
  }, [canCreateCustomers, canReadCustomers]);

  useEffect(() => {
    if (open) {
      return undefined;
    }
    resetForm();
    return undefined;
  }, [open, resetForm]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    let cancelled = false;
    setLoadingData(true);

    const loadData = async () => {
      try {
        const [servicesData, combos, plansData, catalogData] = await Promise.all([
          StoreService.getServices(true),
          ServiceComboService.getServiceComboCatalog(storeId).catch(() => []),
          allowsMemberships
            ? MembershipPlanService.getActivePlans(storeId).catch(() => [])
            : Promise.resolve([]),
          hideProducts ? Promise.resolve([]) : ProductService.getProductCatalog(storeId).catch(() => []),
        ]);
        if (cancelled) return;
        setServices(mergeCatalogCombos(servicesData, combos));
        setMembershipPlans(Array.isArray(plansData) ? plansData : []);
        setCatalogProducts(Array.isArray(catalogData)
          ? catalogData.filter((product) => !product.disabled)
          : []);

        if (initialCustomer?.customerId && !cancelled) {
          setSelectedCustomer(initialCustomer);
          setCustomerType(CUSTOMER_TYPE_EXISTING);
        }

        if (!cancelled && Array.isArray(initialServiceIds) && initialServiceIds.length > 0) {
          setSelectedServiceIds(initialServiceIds.map((id) => Number(id)).filter((id) => Number.isFinite(id)));
        }
      } catch (loadError) {
        console.error('Failed to load booking dialog data:', loadError);
        if (!cancelled) {
          setSubmitError('managementAppointments.book.loadError');
        }
      } finally {
        if (!cancelled) {
          setLoadingData(false);
        }
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [open, storeId, initialCustomer, initialServiceIds, hideProducts, allowsMemberships]);

  useEffect(() => {
    if (!open) {
      return;
    }
    if (selectedServiceIds.length === 0) {
      setServiceProfessionalMap((current) => (Object.keys(current).length === 0 ? current : {}));
      return;
    }

    const servicesById = new Map(services.map((service) => [service.id, service]));
    const memberCanDo = (serviceId) => {
      const service = servicesById.get(serviceId);
      return (member) => (service?.isCombo
        ? memberCanPerformBookingService(member, service)
        : member.services?.some((item) => item.id === serviceId));
    };
    const suggested = computeSuggestedAssignment(activeTeamMembers, selectedServiceIds, servicesById);
    const nextMap = { ...suggested };

    selectedServiceIds.forEach((serviceId) => {
      if (nextMap[serviceId]) {
        return;
      }

      if (slotContext?.teamMemberId) {
        const prefilledCanDo = activeTeamMembers.some((member) => (
          member.id === slotContext.teamMemberId && memberCanDo(serviceId)(member)
        ));
        if (prefilledCanDo) {
          nextMap[serviceId] = slotContext.teamMemberId;
          return;
        }
      }

      if (activeTeamMembers.length === 1) {
        nextMap[serviceId] = activeTeamMembers[0].id;
        return;
      }

      const candidates = activeTeamMembers.filter((member) => memberCanDo(serviceId)(member));
      if (candidates.length === 1 || (hideProfessionalSection && candidates.length > 0)) {
        nextMap[serviceId] = candidates[0].id;
      }
    });

    setServiceProfessionalMap(nextMap);
  }, [open, selectedServiceIds, activeTeamMembers, services, slotContext?.teamMemberId, hideProfessionalSection]);

  useEffect(() => {
    if (!open) {
      setMembershipsReady(false);
      membershipPrefillAppliedRef.current = false;
      return undefined;
    }
    if (!allowsMemberships || customerType !== CUSTOMER_TYPE_EXISTING || !selectedCustomer?.customerId) {
      setCustomerMemberships((current) => (current.length === 0 ? current : []));
      setMembershipsReady(false);
      return undefined;
    }

    let cancelled = false;
    setMembershipsReady(false);
    getManagementMemberships(storeId)
      .then((memberships) => {
        if (!cancelled) {
          setCustomerMemberships(getHeldCustomerMemberships(memberships, selectedCustomer.customerId));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCustomerMemberships([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setMembershipsReady(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, customerType, selectedCustomer, storeId, allowsMemberships]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    if (!allowsMemberships
      || customerType !== CUSTOMER_TYPE_EXISTING
      || !selectedCustomer?.customerId
      || selectedServiceIds.length === 0) {
      setBookingEntitlements((current) => (current.length === 0 ? current : []));
      return undefined;
    }

    let cancelled = false;
    setLoadingEntitlements(true);

    getManagementBookingMembershipEntitlements(storeId, selectedCustomer.customerId, selectedServiceIds)
      .then((entitlements) => {
        if (cancelled) return;
        setBookingEntitlements(entitlements);
      })
      .catch(() => {
        if (!cancelled) {
          setBookingEntitlements([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingEntitlements(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [allowsMemberships, open, customerType, selectedCustomer, selectedServiceIds, storeId]);

  // Packages already paid for can be redeemed even after the store drops to Free.
  useEffect(() => {
    if (!open || customerType !== CUSTOMER_TYPE_EXISTING || !selectedCustomer?.customerId) {
      setPackageEntitlements((current) => (current.length === 0 ? current : []));
      return undefined;
    }

    let cancelled = false;
    ServicePackageService.getManagementBookingEntitlements(storeId, {
      customerId: selectedCustomer.customerId,
      serviceIds: selectedServiceIds,
      date: selectedDateKey,
    })
      .then((entitlements) => {
        if (!cancelled) setPackageEntitlements(entitlements);
      })
      .catch(() => {
        if (!cancelled) setPackageEntitlements([]);
      });

    return () => {
      cancelled = true;
    };
  }, [open, customerType, selectedCustomer, selectedServiceIds, selectedDateKey, storeId]);

  const toggleService = (serviceId) => {
    setSelectedServiceIds((current) => matchServiceComboIds(
      toggleServiceIdWithLocks(current, serviceId, lockedServiceIds),
      services,
      lockedServiceIds,
    ));
    setErrors((current) => ({ ...current, services: null }));
    setSubmitError(null);
  };

  const applyPackageSelection = (nextPackageId) => {
    setSelectedMembershipId('');
    setSelectedMembershipPlanId('');
    setSelectedServicePackageId(nextPackageId || '');
    const nextPackage = packageEntitlements.find((entitlement) => entitlement.customerServicePackageId === nextPackageId);
    if (nextPackage) {
      setSelectedServiceIds((current) => mergeLockedServiceIds(current, getLockedServiceIds(toPackageLockSource(nextPackage))));
    }
  };

  const applyMembershipSelection = (nextMembershipId, nextPlanId) => {
    setSelectedServicePackageId('');
    setSelectedMembershipId(nextMembershipId || '');
    setSelectedMembershipPlanId(nextPlanId || '');
    const nextPlan = membershipPlans.find((plan) => (
      plan.id === nextPlanId
      || customerMemberships.find((membership) => membership.id === nextMembershipId)?.membershipPlanId === plan.id
    ));
    if (nextPlan) {
      setSelectedServiceIds((current) => mergeLockedServiceIds(current, getLockedServiceIds(nextPlan)));
    }
  };

  useEffect(() => {
    if (!open) {
      membershipPrefillAppliedRef.current = false;
      return;
    }

    if (membershipPrefillAppliedRef.current || !membershipsReady) {
      return;
    }

    if (!initialCustomerMembershipId && !initialMembershipPlanId) {
      membershipPrefillAppliedRef.current = true;
      return;
    }

    const selection = resolveInitialMembershipSelection({
      customerMemberships,
      membershipPlans,
      customerMembershipId: initialCustomerMembershipId,
      membershipPlanId: initialMembershipPlanId,
    });

    if (!selection) {
      return;
    }

    membershipPrefillAppliedRef.current = true;
    applyMembershipSelection(selection.membershipId, selection.planId);
  }, [
    open,
    membershipsReady,
    customerMemberships,
    membershipPlans,
    initialCustomerMembershipId,
    initialMembershipPlanId,
  ]);

  const handleCheckEmail = async () => {
    const trimmedEmail = newCustomerEmail.trim();
    if (!trimmedEmail) {
      setErrors((current) => ({ ...current, email: 'customersManagement.create.validation.emailRequired' }));
      return;
    }

    if (!isValidEmail(trimmedEmail)) {
      setErrors((current) => ({ ...current, email: 'customersManagement.create.validation.emailInvalid' }));
      return;
    }

    try {
      setCheckingEmail(true);
      setEmailCheckStatus(EMAIL_CHECK_STATUS.CHECKING);
      setErrors((current) => ({ ...current, email: null, emailCheck: null, contact: null }));
      const result = await StoreCustomerService.checkCustomerEmail(trimmedEmail, storeId);

      if (!result.exists) {
        setEmailCheckStatus(EMAIL_CHECK_STATUS.AVAILABLE);
        setExistingCustomerName('');
        return;
      }

      if (result.alreadyAssignedToStore) {
        setEmailCheckStatus(EMAIL_CHECK_STATUS.ALREADY_IN_STORE);
        setExistingCustomerName(result.name || '');
        return;
      }

      setEmailCheckStatus(EMAIL_CHECK_STATUS.EXISTING);
      setExistingCustomerName(result.name || '');
      setNewCustomerName(result.name || '');
    } catch (checkError) {
      console.error('Failed to check customer email:', checkError);
      setEmailCheckStatus(EMAIL_CHECK_STATUS.ERROR);
      setErrors((current) => ({ ...current, emailCheck: 'customersManagement.create.emailCheck.error' }));
    } finally {
      setCheckingEmail(false);
    }
  };

  const handleCheckPhone = async () => {
    const phoneDto = toPhoneDto(newCustomerPhoneCountryCode, newCustomerPhoneNumber);
    if (!phoneDto) {
      setErrors((current) => ({ ...current, phone: 'customersManagement.create.validation.phoneRequired' }));
      return;
    }

    try {
      setCheckingPhone(true);
      setPhoneCheckStatus(EMAIL_CHECK_STATUS.CHECKING);
      setErrors((current) => ({ ...current, phone: null, phoneCheck: null, contact: null }));
      await StoreCustomerService.checkCustomerPhone(phoneDto, storeId);
      setPhoneCheckStatus(EMAIL_CHECK_STATUS.AVAILABLE);
      setExistingCustomerName('');
    } catch (checkError) {
      console.error('Failed to check customer phone:', checkError);
      setPhoneCheckStatus(EMAIL_CHECK_STATUS.ERROR);
      setErrors((current) => ({ ...current, phoneCheck: 'customersManagement.create.phoneCheck.error' }));
    } finally {
      setCheckingPhone(false);
    }
  };

  const validateForm = () => {
    const nextErrors = {};

    if (selectedServiceIds.length === 0) {
      nextErrors.services = 'managementAppointments.book.validation.servicesRequired';
    }

    if (!selectedDateKey) {
      nextErrors.date = 'managementAppointments.book.validation.dateRequired';
    }

    if (!selectedStartTime) {
      nextErrors.startTime = 'managementAppointments.book.validation.startTimeRequired';
    }

    selectedServiceIds.forEach((serviceId) => {
      if (!serviceProfessionalMap[serviceId]) {
        nextErrors.professionals = 'managementAppointments.book.validation.professionalRequired';
      }
    });

    selectedServices.forEach((service) => {
      if (service.isCombo) return;
      const quote = lineQuotes[service.id];
      if (isLineQuoteDurationInvalid(service, quote)) {
        nextErrors.duration = 'managementAppointments.book.validation.durationRequired';
      }
      if (canSetServicePrices && quote?.priceTouched && quote.unitPrice !== '' && quote.unitPrice != null) {
        const price = Number(quote.unitPrice);
        if (!Number.isFinite(price) || price < 0) {
          nextErrors.linePrice = 'managementAppointments.book.validation.linePriceInvalid';
        }
      }
    });

    if (customerType === CUSTOMER_TYPE_EXISTING && !selectedCustomer) {
      nextErrors.customer = 'managementAppointments.book.validation.customerRequired';
    }

    if (customerType === CUSTOMER_TYPE_NEW) {
      const trimmedEmail = newCustomerEmail.trim();
      const phoneDto = toPhoneDto(newCustomerPhoneCountryCode, newCustomerPhoneNumber);

      if (trimmedEmail) {
        if (!isValidEmail(trimmedEmail)) {
          nextErrors.email = 'customersManagement.create.validation.emailInvalid';
        } else if (emailCheckStatus === EMAIL_CHECK_STATUS.IDLE || emailCheckStatus === EMAIL_CHECK_STATUS.CHECKING) {
          nextErrors.emailCheck = 'customersManagement.create.validation.emailCheckRequired';
        } else if (emailCheckStatus === EMAIL_CHECK_STATUS.ALREADY_IN_STORE) {
          nextErrors.emailCheck = 'customersManagement.create.emailCheck.alreadyInStore';
        } else if (emailCheckStatus === EMAIL_CHECK_STATUS.ERROR) {
          nextErrors.emailCheck = 'customersManagement.create.emailCheck.error';
        } else if (emailCheckStatus === EMAIL_CHECK_STATUS.AVAILABLE && !newCustomerName.trim()) {
          nextErrors.name = 'customersManagement.create.validation.nameRequired';
        }
      } else if (!phoneDto) {
        nextErrors.contact = 'customersManagement.create.validation.contactRequired';
      } else if (phoneCheckStatus === EMAIL_CHECK_STATUS.IDLE || phoneCheckStatus === EMAIL_CHECK_STATUS.CHECKING) {
        nextErrors.phoneCheck = 'customersManagement.create.phoneCheck.required';
      } else if (phoneCheckStatus === EMAIL_CHECK_STATUS.ALREADY_IN_STORE) {
        nextErrors.phoneCheck = 'customersManagement.create.phoneCheck.alreadyInStore';
      } else if (phoneCheckStatus === EMAIL_CHECK_STATUS.ERROR) {
        nextErrors.phoneCheck = 'customersManagement.create.phoneCheck.error';
      } else if (phoneCheckStatus === EMAIL_CHECK_STATUS.AVAILABLE && !newCustomerName.trim()) {
        nextErrors.name = 'customersManagement.create.validation.nameRequired';
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const buildCustomerSelection = () => {
    if (customerType === CUSTOMER_TYPE_EXISTING) {
      return {
        type: CUSTOMER_TYPE_EXISTING,
        customerId: selectedCustomer.customerId,
      };
    }

    if (customerType === CUSTOMER_TYPE_NEW) {
      const trimmedEmail = newCustomerEmail.trim();
      return {
        type: CUSTOMER_TYPE_NEW,
        email: trimmedEmail || null,
        name: newCustomerName.trim(),
        phone: toPhoneDto(newCustomerPhoneCountryCode, newCustomerPhoneNumber),
      };
    }

    return {
      type: CUSTOMER_TYPE_ANONYMOUS,
      guestName: guestName.trim() || undefined,
    };
  };

  const handleSubmit = async () => {
    if (!validateForm() || !selectedDateKey || !selectedStartTime) {
      return;
    }

    const serviceAssignments = withLineQuotes(
      buildPerServiceAssignments(
        quotedServices,
        serviceProfessionalMap,
        selectedStartTime,
      ),
      lineQuotes,
      canSetServicePrices,
    );

    if (serviceAssignments.length !== selectedServiceIds.length) {
      setSubmitError('managementAppointments.book.validation.professionalRequired');
      return;
    }

    try {
      setSubmitting(true);
      setSubmitError(null);

      await createManagementAppointment({
        date: selectedDateKey,
        serviceAssignments,
        detailsFromCustomer: detailsFromCustomer.trim() || undefined,
        ...buildManagementAppointmentMembershipFields({
          customerType,
          selectedMembershipId,
          selectedMembershipPlanId,
          selectedServicePackageId,
        }),
        products: productLines.length
          ? productLines.map((line) => ({ productId: line.productId, quantity: line.quantity }))
          : undefined,
        customerSelection: buildCustomerSelection(),
      });

      onBooked?.();
      onClose();
    } catch (submitErr) {
      console.error('Failed to book appointment:', submitErr);
      const apiMessage = submitErr?.response?.data?.message || '';
      const stockMessage = describeInsufficientStock(submitErr, t);
      const entitlementErrorKey = MANAGEMENT_ENTITLEMENT_ERROR_KEYS[getApiErrorCode(submitErr)];
      if (stockMessage) {
        setSubmitError(stockMessage);
      } else if (entitlementErrorKey) {
        setSubmitError(entitlementErrorKey);
      } else if (apiMessage.toLowerCase().includes('no longer available')) {
        setSubmitError('managementAppointments.book.warnings.slotConflict');
      } else {
        setSubmitError(apiMessage || 'managementAppointments.book.submitError');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const renderCustomerSection = () => {
    if (customerType === CUSTOMER_TYPE_EXISTING) {
      if (!canReadCustomers) {
        return (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('managementAppointments.book.noCustomerReadPermission')}
          </Alert>
        );
      }

      return (
        <Box sx={{ mb: 2 }}>
          <CustomerDropdown
            storeId={storeId}
            enabled={open}
            value={selectedCustomer}
            onChange={(value) => {
              setSelectedCustomer(value);
              setSelectedMembershipId('');
              setSelectedMembershipPlanId('');
              setSelectedServicePackageId('');
              setErrors((current) => ({ ...current, customer: null }));
            }}
            placeholder={t('customersManagement.dropdown.searchPlaceholder')}
            error={Boolean(errors.customer)}
            helperText={errors.customer ? t(errors.customer) : ''}
          />
        </Box>
      );
    }

    if (customerType === CUSTOMER_TYPE_NEW) {
      const trimmedEmail = newCustomerEmail.trim();
      const usingEmail = Boolean(trimmedEmail);
      const isPhoneOnlyPath = !usingEmail;
      const phoneDto = toPhoneDto(newCustomerPhoneCountryCode, newCustomerPhoneNumber);
      const isChecking = checkingEmail || checkingPhone;
      const nameEnabled = isPhoneOnlyPath
        ? phoneCheckStatus !== EMAIL_CHECK_STATUS.EXISTING
          && phoneCheckStatus !== EMAIL_CHECK_STATUS.ALREADY_IN_STORE
        : emailCheckStatus === EMAIL_CHECK_STATUS.AVAILABLE
          || emailCheckStatus === EMAIL_CHECK_STATUS.EXISTING;
      const showNameField = isPhoneOnlyPath
        || emailCheckStatus === EMAIL_CHECK_STATUS.AVAILABLE
        || emailCheckStatus === EMAIL_CHECK_STATUS.EXISTING;
      const checkButtonSx = {
        flexShrink: 0,
        minHeight: 48,
        minWidth: 100,
        borderColor: BRAND.accent,
        color: BRAND.accentText,
        '&:hover': { borderColor: BRAND.accentDark, backgroundColor: brandAlpha('accent', 0.04) },
      };

      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={inputWrapperSx}>
            <Typography variant="subtitle2" sx={fieldLabelSx}>
              {t('customersManagement.create.email')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <input
                type="email"
                value={newCustomerEmail}
                onChange={(event) => {
                  setNewCustomerEmail(event.target.value);
                  setEmailCheckStatus(EMAIL_CHECK_STATUS.IDLE);
                  setExistingCustomerName('');
                  setErrors((current) => ({ ...current, email: null, emailCheck: null, contact: null }));
                }}
                style={inputStyle(Boolean(errors.email), isChecking)}
                disabled={isChecking || submitting}
              />
              <Button
                variant="outlined"
                onClick={handleCheckEmail}
                disabled={isChecking || submitting || !trimmedEmail}
                sx={checkButtonSx}
              >
                {checkingEmail ? <CircularProgress size={20} /> : t('customersManagement.create.emailCheck.check')}
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {t('customersManagement.create.emailOptional')}
            </Typography>
            {errors.email && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                {t(errors.email)}
              </Typography>
            )}
            {errors.emailCheck && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                {t(errors.emailCheck)}
              </Typography>
            )}
            {errors.contact && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                {t(errors.contact)}
              </Typography>
            )}
            {usingEmail && emailCheckStatus === EMAIL_CHECK_STATUS.AVAILABLE && (
              <Alert severity="success" sx={{ mt: 1 }}>
                {t('customersManagement.create.emailCheck.available')}
              </Alert>
            )}
            {usingEmail && emailCheckStatus === EMAIL_CHECK_STATUS.EXISTING && existingCustomerName && (
              <Alert severity="info" sx={{ mt: 1 }}>
                {t('customersManagement.create.emailCheck.existingConfirmed', { name: existingCustomerName })}
              </Alert>
            )}
            {usingEmail && emailCheckStatus === EMAIL_CHECK_STATUS.ALREADY_IN_STORE && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {t('customersManagement.create.emailCheck.alreadyInStore')}
              </Alert>
            )}
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={fieldLabelSx}>
              {t('customersManagement.create.phone')}{isPhoneOnlyPath ? ' *' : ''}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <SalovarePhoneInput
                  countryCode={newCustomerPhoneCountryCode}
                  number={newCustomerPhoneNumber}
                  onCountryCodeChange={(value) => {
                    setNewCustomerPhoneCountryCode(value);
                    setPhoneCheckStatus(EMAIL_CHECK_STATUS.IDLE);
                    setExistingCustomerName('');
                    setErrors((current) => ({ ...current, phone: null, phoneCheck: null, contact: null }));
                  }}
                  onNumberChange={(value) => {
                    setNewCustomerPhoneNumber(value);
                    setPhoneCheckStatus(EMAIL_CHECK_STATUS.IDLE);
                    setExistingCustomerName('');
                    setErrors((current) => ({ ...current, phone: null, phoneCheck: null, contact: null }));
                  }}
                  placeholder={t('customersManagement.create.phonePlaceholder')}
                  disabled={isChecking || submitting}
                  error={Boolean(errors.phone)}
                  autoCompleteSection="book-new-customer"
                />
              </Box>
              {isPhoneOnlyPath ? (
                <Button
                  variant="outlined"
                  onClick={handleCheckPhone}
                  disabled={isChecking || submitting || !phoneDto}
                  sx={checkButtonSx}
                >
                  {checkingPhone ? <CircularProgress size={20} /> : t('customersManagement.create.emailCheck.check')}
                </Button>
              ) : null}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {t('customersManagement.create.phoneOptional')}
            </Typography>
            {errors.phone && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                {t(errors.phone)}
              </Typography>
            )}
            {errors.phoneCheck && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                {t(errors.phoneCheck)}
              </Typography>
            )}
            {isPhoneOnlyPath && phoneCheckStatus === EMAIL_CHECK_STATUS.AVAILABLE && (
              <Alert severity="success" sx={{ mt: 1 }}>
                {t('customersManagement.create.phoneCheck.available')}
              </Alert>
            )}
            {isPhoneOnlyPath && phoneCheckStatus === EMAIL_CHECK_STATUS.EXISTING && existingCustomerName && (
              <Alert severity="info" sx={{ mt: 1 }}>
                {t('customersManagement.create.phoneCheck.existingConfirmed', { name: existingCustomerName })}
              </Alert>
            )}
            {isPhoneOnlyPath && phoneCheckStatus === EMAIL_CHECK_STATUS.ALREADY_IN_STORE && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {t('customersManagement.create.phoneCheck.alreadyInStore')}
              </Alert>
            )}
          </Box>

          {showNameField ? (
            <Box sx={inputWrapperSx}>
              <Typography variant="subtitle2" sx={fieldLabelSx}>
                {t('customersManagement.create.name')} *
              </Typography>
              <input
                type="text"
                value={newCustomerName}
                onChange={(event) => {
                  setNewCustomerName(event.target.value);
                  setErrors((current) => ({ ...current, name: null }));
                }}
                style={inputStyle(Boolean(errors.name), !nameEnabled || submitting)}
                disabled={!nameEnabled || submitting}
              />
              {errors.name && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                  {t(errors.name)}
                </Typography>
              )}
            </Box>
          ) : null}
        </Box>
      );
    }

    return (
      <Box sx={inputWrapperSx}>
        <Typography variant="subtitle2" sx={fieldLabelSx}>
          {t('managementAppointments.book.guestName')}
        </Typography>
        <input
          type="text"
          value={guestName}
          onChange={(event) => setGuestName(event.target.value)}
          placeholder={t('managementAppointments.book.guestNamePlaceholder')}
          style={inputStyle(false, submitting)}
          disabled={submitting}
        />
      </Box>
    );
  };

  return (
    <Drawer
      anchor={isMobile ? 'bottom' : 'right'}
      open={open}
      onClose={onClose}
      disableScrollLock
      PaperProps={{
        sx: {
          width: isMobile ? '100%' : { sm: 520, md: 620 },
          maxWidth: '100vw',
          height: isMobile ? 'min(92dvh, 920px)' : '100%',
          maxHeight: isMobile ? '92dvh' : '100%',
          borderTopLeftRadius: isMobile ? 20 : 0,
          borderTopRightRadius: isMobile ? 20 : 0,
          overflow: 'hidden',
          background: BRAND.surface,
          boxShadow: isMobile
            ? `0 -12px 40px ${brandAlpha('primary', 0.16)}`
            : `0 0 40px ${brandAlpha('primary', 0.12)}`,
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            position: 'relative',
            flexShrink: 0,
            p: { xs: 2.25, sm: 3 },
            background: BRAND.primary,
          }}
        >
          <IconButton
            onClick={onClose}
            aria-label={t('common.close')}
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              color: BRAND.onPrimary,
              bgcolor: alpha('#000', 0.12),
              '&:hover': { bgcolor: alpha('#000', 0.2) },
            }}
          >
            <CloseIcon />
          </IconButton>

          <Box sx={{ pr: 5 }}>
            <Typography
              variant="overline"
              sx={{ color: BRAND.onPrimaryMuted, letterSpacing: '0.14em', fontWeight: 700 }}
            >
              {t('managementAppointments.book.title')}
            </Typography>
            <Typography
              variant="h4"
              sx={{
                color: BRAND.onPrimary,
                fontWeight: 800,
                lineHeight: 1.1,
                fontSize: { xs: '1.65rem', sm: '2rem' },
                mt: 0.25,
              }}
            >
              {selectedStartTime || slotContext?.startTime || '—'}
            </Typography>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.75, color: BRAND.onPrimaryMuted }}>
              <CalendarMonthIcon sx={{ fontSize: 16 }} />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {formatCalendarDate(selectedDateKey || slotContext?.date)}
                {slotContext?.teamMemberName ? ` · ${slotContext.teamMemberName}` : ''}
              </Typography>
            </Stack>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch',
            px: { xs: 2, sm: 3 },
            py: { xs: 2.25, sm: 3 },
          }}
        >
          {loadingData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={2}>
              {submitError && (
                <Alert severity="error">
                  {t(submitError, { defaultValue: submitError })}
                </Alert>
              )}

              <Paper
                elevation={0}
                sx={{
                  borderRadius: BRAND_RADIUS.card,
                  border: `1px solid ${BRAND.line}`,
                  bgcolor: BRAND.surface,
                  overflow: 'hidden',
                }}
              >
            <Box
              sx={{
                px: 2,
                py: 1.5,
                background: BRAND.background,
                borderBottom: `1px solid ${BRAND.lineSoft}`,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: BRAND.primaryText }}>
                    {t('managementAppointments.book.appointmentContext')}
                  </Typography>
                  {slotContext?.teamMemberName ? (
                    <Typography variant="body2" sx={{ color: 'text.secondary', opacity: 0.95 }}>
                      {slotContext.teamMemberName}
                    </Typography>
                  ) : null}
                </Stack>
                <Box sx={{ width: 34, height: 34 }} />
              </Stack>
            </Box>

            <Box sx={{ px: 2, py: 1.5 }}>
              <Box
                role="button"
                tabIndex={0}
                onClick={() => setDatePickerOpen(true)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setDatePickerOpen(true);
                  }
                }}
                sx={{ cursor: 'pointer', mb: 1.5 }}
              >
                <Stack
                  ref={dateAnchorRef}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                    <CalendarMonthIcon sx={{ fontSize: 18, color: BRAND.accentText }} />
                    <Typography variant="h6" sx={{ fontWeight: 900, color: BRAND.text, lineHeight: 1.1 }}>
                      {formatCalendarDate(selectedDateKey)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', opacity: 0.95, whiteSpace: 'nowrap' }}>
                      {t('managementAppointments.book.tapToChangeTime')}
                    </Typography>
                  </Stack>
                  <Box sx={{ width: 18, height: 18 }} />
                </Stack>
                {errors.date ? (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {t(errors.date)}
                  </Typography>
                ) : null}
              </Box>

              <SalovareDatePicker
                hideTextField
                open={datePickerOpen}
                onOpen={() => setDatePickerOpen(true)}
                onClose={() => setDatePickerOpen(false)}
                value={selectedDate}
                onChange={(value) => {
                  if (!value) {
                    return;
                  }
                  setSelectedDate(value);
                  setErrors((current) => ({ ...current, date: null }));
                  setSubmitError(null);
                }}
                slotProps={{
                  popper: {
                    anchorEl: dateAnchorRef.current,
                  },
                }}
              />

              <Box
                role="button"
                tabIndex={0}
                onClick={() => setStartTimeMenuOpen(true)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setStartTimeMenuOpen(true);
                  }
                }}
                sx={{ cursor: 'pointer' }}
              >
                <Stack
                  ref={startTimeAnchorRef}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="space-between"
                  onClick={(event) => {
                    event.stopPropagation();
                    setStartTimeMenuOpen(true);
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                    <AccessTimeIcon sx={{ fontSize: 18, color: BRAND.accentText }} />
                    <Typography variant="h6" sx={{ fontWeight: 900, color: BRAND.text, lineHeight: 1.1 }}>
                      {selectedStartTime || slotContext?.startTime || '—'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', opacity: 0.95, whiteSpace: 'nowrap' }}>
                      {t('managementAppointments.book.tapToChangeTime')}
                    </Typography>
                  </Stack>
                  <Box sx={{ width: 18, height: 18 }} />
                </Stack>
                {errors.startTime ? (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {t(errors.startTime)}
                  </Typography>
                ) : null}
              </Box>

              <Menu
                open={startTimeMenuOpen}
                onClose={() => setStartTimeMenuOpen(false)}
                anchorEl={startTimeAnchorRef.current}
                disableScrollLock
                MenuListProps={{ dense: true }}
                PaperProps={{
                  sx: {
                    mt: 1,
                    maxHeight: 360,
                    borderRadius: 2,
                    border: `1px solid ${BRAND.line}`,
                    boxShadow: `0 18px 48px ${brandAlpha('primary', 0.14)}`,
                  },
                }}
              >
                {MANAGEMENT_START_TIME_OPTIONS.map((time) => (
                  <MenuItem
                    key={time}
                    selected={time === selectedStartTime}
                    onClick={() => {
                      setSelectedStartTime(time);
                      setErrors((current) => ({ ...current, startTime: null }));
                      setSubmitError(null);
                      setStartTimeMenuOpen(false);
                    }}
                    sx={{ fontWeight: time === selectedStartTime ? 800 : 600 }}
                  >
                    {time}
                  </MenuItem>
                ))}
              </Menu>
            </Box>
              </Paper>

              {bookingWarning && (
                <Alert severity="warning">
                  {t(`managementAppointments.book.warnings.${bookingWarning}`)}
                </Alert>
              )}

              <Paper
                elevation={0}
                sx={{
                  borderRadius: BRAND_RADIUS.card,
                  border: `1px solid ${BRAND.line}`,
                  bgcolor: BRAND.surface,
                  p: 2,
                }}
              >
            <Typography variant="subtitle2" sx={{ ...fieldLabelSx, mb: 1 }}>
              {t('managementAppointments.book.customerType')}
            </Typography>
            <RadioGroup
              row
              value={customerType}
              onChange={(event) => {
                setCustomerType(event.target.value);
                setSelectedCustomer(null);
                setCustomerMemberships([]);
                setSelectedMembershipId('');
                setSelectedMembershipPlanId('');
                setErrors({});
                setSubmitError(null);
              }}
            >
              {canReadCustomers && (
                <FormControlLabel value={CUSTOMER_TYPE_EXISTING} control={<Radio />} label={t('managementAppointments.book.customerTypeExisting')} />
              )}
              {canCreateCustomers && (
                <FormControlLabel value={CUSTOMER_TYPE_NEW} control={<Radio />} label={t('managementAppointments.book.customerTypeNew')} />
              )}
              <FormControlLabel value={CUSTOMER_TYPE_ANONYMOUS} control={<Radio />} label={t('managementAppointments.book.customerTypeAnonymous')} />
            </RadioGroup>
            <Box sx={{ mt: 2 }}>
              {renderCustomerSection()}
            </Box>
              </Paper>

              {showMembershipSection && (
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: BRAND_RADIUS.card,
                    border: `1px solid ${BRAND.line}`,
                    bgcolor: BRAND.surface,
                    p: 2,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ ...fieldLabelSx, mb: 1 }}>
                    {t(hasPackageOptions ? 'managementAppointments.book.membershipOrPackage' : 'managementAppointments.book.membership')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {t(hasPackageOptions ? 'managementAppointments.book.membershipOrPackageHelp' : 'managementAppointments.book.membershipHelp')}
                  </Typography>
                  {loadingEntitlements ? (
                    <CircularProgress size={24} />
                  ) : (
                    <RadioGroup
                      sx={{ width: '100%' }}
                      value={
                        (selectedServicePackageId && `${PACKAGE_OPTION_PREFIX}${selectedServicePackageId}`)
                        || (selectedMembershipPlanId && `plan:${selectedMembershipPlanId}`)
                        || (selectedMembershipId && `membership:${selectedMembershipId}`)
                        || ''
                      }
                      onChange={(event) => {
                        const value = event.target.value;
                        if (value.startsWith(PACKAGE_OPTION_PREFIX)) {
                          applyPackageSelection(value.slice(PACKAGE_OPTION_PREFIX.length));
                          return;
                        }
                        if (value.startsWith('plan:')) {
                          applyMembershipSelection('', value.slice(5));
                          return;
                        }
                        if (value.startsWith('membership:')) {
                          applyMembershipSelection(value.slice(11), '');
                          return;
                        }
                        applyMembershipSelection('', '');
                      }}
                    >
                      <FormControlLabel
                        value=""
                        control={<Radio />}
                        label={t(hasPackageOptions
                          ? 'managementAppointments.book.noEntitlementRedemption'
                          : 'managementAppointments.book.noMembershipRedemption')}
                      />
                      {packageEntitlements.map((entitlement) => (
                        <FormControlLabel
                          key={entitlement.customerServicePackageId}
                          value={`${PACKAGE_OPTION_PREFIX}${entitlement.customerServicePackageId}`}
                          control={<Radio />}
                          disabled={entitlement.isExpiredForDate}
                          sx={membershipOptionLabelSx}
                          label={(
                            <MembershipBookingOptionLabel
                              name={getLocalizedValue(
                                entitlement.name,
                                lang,
                                normalizedStoreLanguage,
                                t('customer.packages.card.unnamed'),
                              )}
                              tag={entitlement.isExpiredForDate
                                ? t('managementAppointments.book.packageExpiredTag')
                                : t('managementAppointments.book.packageTag', { count: entitlement.remainingQuantity })}
                              variant={entitlement.isExpiredForDate ? 'enroll' : 'subscribed'}
                            />
                          )}
                        />
                      ))}
                      {customerMemberships.map((membership) => (
                        <FormControlLabel
                          key={membership.id}
                          value={`membership:${membership.id}`}
                          control={<Radio />}
                          sx={membershipOptionLabelSx}
                          label={(
                            <MembershipBookingOptionLabel
                              name={getLocalizedValue(
                                membership.planName,
                                lang,
                                normalizedStoreLanguage,
                                t('customer.memberships.card.unnamed'),
                              )}
                              tag={t('managementAppointments.book.subscribedTag')}
                              variant="subscribed"
                            />
                          )}
                        />
                      ))}
                      {enrollablePlans.map((plan) => (
                        <FormControlLabel
                          key={plan.id}
                          value={`plan:${plan.id}`}
                          control={<Radio />}
                          sx={membershipOptionLabelSx}
                          label={(
                            <MembershipBookingOptionLabel
                              name={getLocalizedValue(
                                plan.name,
                                lang,
                                normalizedStoreLanguage,
                                t('customer.memberships.card.unnamed'),
                              )}
                              tag={t('managementAppointments.book.enrollInStoreTag')}
                              variant="enroll"
                            />
                          )}
                        />
                      ))}
                    </RadioGroup>
                  )}
                  {selectedMembershipPlanId && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      {t('managementAppointments.book.pendingEnrollmentHelp')}
                    </Typography>
                  )}
                </Paper>
              )}

              <Paper
                elevation={0}
                sx={{
                  borderRadius: BRAND_RADIUS.card,
                  border: `1px solid ${BRAND.line}`,
                  bgcolor: BRAND.surface,
                  p: 2,
                }}
              >
            <Typography variant="subtitle2" sx={{ ...fieldLabelSx, mb: 1 }}>
              {t('managementAppointments.book.services')} *
            </Typography>
            {entitlementLockSource && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {t(selectedPlan
                  ? 'managementAppointments.book.planServicesLocked'
                  : 'managementAppointments.book.packageServicesLocked')}
              </Typography>
            )}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {services.map((service) => {
                const selected = selectedServiceIds.includes(service.id);
                const locked = selected && lockedServiceIds.has(Number(service.id));
                return (
                  <Chip
                    key={service.id}
                    label={getServiceName(service.name, lang, normalizedStoreLanguage)}
                    onClick={locked ? undefined : () => toggleService(service.id)}
                    color={selected ? 'primary' : 'default'}
                    variant={selected ? 'filled' : 'outlined'}
                    sx={selected ? { bgcolor: BRAND.accent, color: BRAND.onAccent } : undefined}
                  />
                );
              })}
            </Box>
            {errors.services && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                {t(errors.services)}
              </Typography>
            )}
            {customerType === CUSTOMER_TYPE_EXISTING && selectedCustomer?.customerId ? (
              <PendingConsentAlert
                id="book-appointment-consent-alert"
                storeId={storeId}
                customerId={selectedCustomer.customerId}
                serviceIds={selectedServices.filter((service) => !service.isCombo).map((service) => service.id)}
                serviceComboIds={selectedServices.filter((service) => service.isCombo).map((service) => service.id)}
                sx={{ mt: 1.5 }}
              />
            ) : null}
              </Paper>

              {selectedServiceIds.length > 0 && !hideProfessionalSection && (
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: BRAND_RADIUS.card,
                    border: `1px solid ${BRAND.line}`,
                    bgcolor: BRAND.surface,
                    p: 2,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ ...fieldLabelSx, mb: 1 }}>
                    {t('managementAppointments.book.professionals')}
                  </Typography>
              {selectedServices.map((service) => {
                const candidates = getProfessionalsForManagedBookingService(
                  activeTeamMembers,
                  service.id,
                  entitlementLockSource,
                  service,
                );
                const options = service.isCombo || candidates.length > 0 ? candidates : activeTeamMembers;

                return (
                  <SalovareSelectField
                    key={service.id}
                    label={getServiceName(service.name, lang, normalizedStoreLanguage)}
                    required
                    value={serviceProfessionalMap[service.id] || ''}
                    onChange={(event) => {
                      setServiceProfessionalMap((current) => ({
                        ...current,
                        [service.id]: event.target.value,
                      }));
                      setErrors((current) => ({ ...current, professionals: null }));
                    }}
                    displayEmpty
                    renderValue={(value) => {
                      if (!value) {
                        return (
                          <Typography component="span" sx={{ color: BRAND.textSubtle }}>
                            {t('managementAppointments.book.selectProfessional')}
                          </Typography>
                        );
                      }
                      return options.find((member) => member.id === value)?.name || '';
                    }}
                  >
                    {options.map((member) => (
                      <MenuItem key={member.id} value={member.id}>
                        {member.name}
                      </MenuItem>
                    ))}
                  </SalovareSelectField>
                );
              })}
              {errors.professionals && (
                <Typography variant="caption" color="error">
                  {t(errors.professionals)}
                </Typography>
              )}
                </Paper>
              )}

              {selectedServices.some((service) => !service.isCombo) ? (
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: BRAND_RADIUS.card,
                    border: `1px solid ${BRAND.line}`,
                    bgcolor: BRAND.surface,
                    p: 2,
                  }}
                >
                  {selectedServices.filter((service) => !service.isCombo).map((service) => (
                    <AppointmentLineQuoteFields
                      key={service.id}
                      service={service}
                      serviceName={getServiceName(service.name, lang, normalizedStoreLanguage)}
                      quote={lineQuotes[service.id]}
                      canSetPrice={canSetServicePrices}
                      currency={currencyCode}
                      priceHint={assignedServicePriceHint(
                        service,
                        activeTeamMembers.find((member) => member.id === serviceProfessionalMap[service.id]),
                      )}
                      onChange={(next) => setLineQuotes((current) => ({ ...current, [service.id]: next }))}
                    />
                  ))}
                  {errors.duration ? (
                    <Typography variant="caption" color="error">{t(errors.duration)}</Typography>
                  ) : null}
                  {errors.linePrice ? (
                    <Typography variant="caption" color="error">{t(errors.linePrice)}</Typography>
                  ) : null}
                </Paper>
              ) : null}

              {!hideProducts && catalogProducts.length > 0 && (
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: BRAND_RADIUS.card,
                    border: `1px solid ${BRAND.line}`,
                    bgcolor: BRAND.surface,
                    p: 2,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ ...fieldLabelSx, mb: 1 }}>
                    {t('managementAppointments.book.products')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {t('managementAppointments.book.productsHelp')}
                  </Typography>
                  <ProductSaleLinesField
                    products={catalogProducts}
                    lines={productLines}
                    onChange={setProductLines}
                    currencyCode={currencyCode}
                    disableOutOfStock
                  />
                </Paper>
              )}

              <Paper
                elevation={0}
                sx={{
                  borderRadius: BRAND_RADIUS.card,
                  border: `1px solid ${BRAND.line}`,
                  bgcolor: BRAND.surface,
                  p: 2,
                }}
              >
                <Typography variant="subtitle2" sx={{ ...fieldLabelSx, mb: 1 }}>
                  {t('managementAppointments.book.notes')}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  value={detailsFromCustomer}
                  onChange={(event) => setDetailsFromCustomer(event.target.value)}
                  placeholder={t('managementAppointments.book.notesPlaceholder')}
                />
              </Paper>
            </Stack>
          )}
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            px: { xs: 2, sm: 3 },
            pt: 1.5,
            pb: {
              xs: 'max(12px, env(safe-area-inset-bottom, 0px))',
              sm: 2,
            },
            borderTop: `1px solid ${BRAND.line}`,
            bgcolor: BRAND.surface,
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'stretch' : 'center',
            justifyContent: 'space-between',
            gap: isMobile ? 1.25 : 2,
            minWidth: 0,
          }}
        >
          {!isMobile && (
            <Button color="inherit" onClick={onClose} disabled={submitting}>
              {t('common.cancel')}
            </Button>
          )}
          <Box
            sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: isMobile ? 'stretch' : 'center',
              gap: isMobile ? 1 : 2,
              minWidth: 0,
              width: isMobile ? '100%' : 'auto',
            }}
          >
            {selectedServiceIds.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: isMobile ? 'center' : 'flex-end',
                  gap: 0.25,
                  minWidth: 0,
                }}
              >
                {additionalCostAmount > 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: BRAND.textMuted,
                      textAlign: isMobile ? 'center' : 'right',
                    }}
                  >
                    {t('managementAppointments.additionalCost')}: {formatCurrency(additionalCostAmount, currencyCode)}
                  </Typography>
                )}
                {!hideProducts && productsTotal > 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: BRAND.textMuted,
                      textAlign: isMobile ? 'center' : 'right',
                    }}
                  >
                    {t('managementAppointments.products')}: {formatCurrency(productsTotal, currencyCode)}
                  </Typography>
                )}
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    color: BRAND.primaryText,
                    whiteSpace: 'nowrap',
                    textAlign: isMobile ? 'center' : 'right',
                  }}
                >
                  {t('managementAppointments.amountDue')}: {formatCurrency(amountDue, currencyCode)}
                </Typography>
              </Box>
            )}
            <PrimaryActionButton
              onClick={handleSubmit}
              disabled={submitting || loadingData}
              fullWidth={isMobile}
              sx={{ minWidth: isMobile ? 0 : 200 }}
            >
              {submitting ? t('managementAppointments.book.submitting') : t('managementAppointments.book.submit')}
            </PrimaryActionButton>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default BookAppointmentDialog;
